import { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentPreview,
  MiLoader,
  MiStackedTypography,
} from "@miplatform/components";
import { DATE_TIME_FORMATS, FILE_MIME_TYPES } from "@miplatform/constants";
import { formatDate, getCurrencyString } from "@miplatform/utils";
import { Box } from "@mui/material";
import { useReconcileContext } from "../../ReconcileContext";

export const InvoiceDisplay = memo(({ invoice }) => {
  const { classes } = useStyles();
  const {
    invoicePdfs = [],
    formatVarianceDisplay,
    isPdfLoading,
    getVarianceColor,
  } = useReconcileContext();

  const invoicePdf = useMemo(() => {
    return invoicePdfs?.find(
      (pdf) => pdf.id === invoice?.invoiceItems[0]?.invoiceId
    );
  }, [invoice?.value, invoicePdfs]);

  const invoiceTotals = useMemo(() => {
    if (!invoice?.invoiceItems) {
      return { amount: 0, variance: 0 };
    }

    return invoice.invoiceItems.reduce(
      (acc, item) => ({
        amount: acc.amount + (item?.totalPrice || 0),
        variance: acc.variance + (item?.variance || 0),
      }),
      { amount: 0, variance: 0 }
    );
  }, [invoice?.invoiceItems]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" p={2} pt={1} mb={2}>
        <Box display="flex" gap={4}>
          <MiStackedTypography
            title="Created By"
            description={formatDate(
              invoice?.createdOn,
              DATE_TIME_FORMATS.FORMAT_MM_DD_YY
            )}
            descriptionProps={{
              className: classes.fieldDescription,
            }}
          />
          <MiStackedTypography
            title="MV Invoice #"
            description={invoice?.invoiceNumber}
            descriptionProps={{
              className: classes.fieldDescription,
            }}
          />
          <MiStackedTypography
            title="Invoice Amount"
            description={getCurrencyString(invoiceTotals.amount)}
            descriptionProps={{
              className: classes.fieldDescription,
            }}
          />
          <MiStackedTypography
            title="Variance"
            description={formatVarianceDisplay(invoiceTotals.variance)}
            descriptionProps={{
              className: classes.fieldDescription,
            }}
            color={getVarianceColor(invoiceTotals.variance)}
          />
          {isPdfLoading && (
            <Box>
              <MiStackedTypography title="Loading PDF..." />
              <MiLoader
                style={{ marginTop: 8 }}
                type="linear"
                isLoading={isPdfLoading}
                size={20}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box position="relative" width="100%" height="100%">
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <DocumentPreview
            selectedDocument={{
              name: `${(invoice?.label || "").trim()}.pdf`,
              contentType: FILE_MIME_TYPES.PDF,
              base64Data: invoicePdf?.pdfFile,
            }}
          />
        </Box>
      </Box>
    </>
  );
});

const useStyles = makeStyles()((theme) => ({
  fieldDescription: {
    maxWidth: 110,
    fontWeight: theme.fontWeight.bold,
  },
}));
